import { useState, useMemo } from "react";
import { Container, LoadingBlock } from "@/components/atoms";
import {
  ProductCard,
  FilterPillRow,
  Breadcrumb,
  EmptyState,
  type FilterOption,
} from "@/components/molecules";
import { useProducts } from "@/hooks/use-products";
import { useCategories } from "@/hooks/use-categories";
import { Seo } from "@/components/seo";

// Shop All — product grid + category filter pills (frame 49:2).
export default function Shop() {
  const { data: products, isLoading } = useProducts();
  const { data: categories = [] } = useCategories();
  const [filter, setFilter] = useState("all");

  const populatedSlugs = useMemo(
    () => new Set((products ?? []).map((p) => p.category)),
    [products],
  );

  const options: FilterOption[] = useMemo(
    () => [
      { value: "all", label: "All" },
      ...categories
        .filter((c) => c.active && populatedSlugs.has(c.slug))
        .map((c) => ({ value: c.slug, label: c.name })),
    ],
    [categories, populatedSlugs],
  );

  const visible = useMemo(
    () =>
      (products ?? []).filter((p) => filter === "all" || p.category === filter),
    [products, filter],
  );

  return (
    <>
      <Seo
        title="Shop All Products"
        path="/shop"
        description="Browse the full DrBurns’ ReLeaf collection of physician-formulated CBD products."
      />

      <Container className="py-8">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Shop" }]} />
        <h1 className="mt-4 font-condensed text-4xl font-bold text-night-blue">
          Shop All Products
        </h1>
        <p className="mt-2 max-w-xl text-night-blue/70">
          Physician-formulated CBD, tested to a 99.5% accuracy rating. Filter by
          what you’re looking for.
        </p>

        <div className="mt-6">
          <FilterPillRow
            options={options}
            active={filter}
            onChange={setFilter}
          />
        </div>

        {isLoading ? (
          <LoadingBlock />
        ) : visible.length === 0 ? (
          <EmptyState
            title="No products here yet"
            description="Try a different filter."
          />
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {visible.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </Container>
    </>
  );
}
